import { Button, Drawer, Flex, LoadingOverlay, Text } from '@mantine/core';
import { CreatableMultiSelect } from '@dealstack/frontend/shared/components/forms/CreatableMultiSelect/CreatableMultiSelect.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { TagRes, MipClassifierRes } from '@dealstack/shared/lib/generated';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { useManageCohortTagsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/components/ManageCohortTagsDrawer/useManageCohortTagsDrawer.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	cohort: MipClassifierRes | null;
	mipId: string;
	allTags: TagRes[];
	onSaved: () => void;
}

export const ManageCohortTagsDrawer = ({
	opened,
	onClose,
	cohort,
	mipId,
	allTags,
	onSaved,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const {
		options,
		selected,
		setSelected,
		handleCreate,
		shouldCreate,
		handleSave,
		isDirty,
		isSaving,
	} = useManageCohortTagsDrawer({ opened, cohort, mipId, allTags, onSaved });

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			size="md"
			transitionProps={drawerTransitionProps}
			title={tMipManage('root.classifiers.cohort_tags.drawer.title')}
		>
			<Flex direction="column" h="100%" mih={400}>
				<LoadingOverlay visible={isSaving} overlayProps={{ blur: 2 }} />

				<Flex direction="column" gap="1rem" style={{ flex: 1 }}>
					<CreatableMultiSelect
						label={tMipManage('root.classifiers.cohort_tags.drawer.label')}
						placeholder={tMipManage('root.classifiers.cohort_tags.drawer.placeholder')}
						size="md"
						data={options}
						value={selected}
						onChange={setSelected}
						clearable
						shouldCreate={shouldCreate}
						onCreate={handleCreate}
						getCreateLabel={(query) => (
							<Flex align="center" gap="0.5rem">
								<Icon variant="add" />
								<Text>
									{tMipManage('root.classifiers.cohort_tags.drawer.create_label')}
								</Text>
								<Text>"{query}"</Text>
							</Flex>
						)}
						disabled={isSaving}
					/>
				</Flex>

				<Flex direction="column" align="flex-start" gap="1rem" mt="auto">
					<Button
						fullWidth
						onClick={handleSave}
						disabled={!isDirty || isSaving}
						loading={isSaving}
					>
						{tMipManage('root.classifiers.cohort_tags.drawer.save_button')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
